package ru.education.building.schedule

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.BottomSheetDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.Color
import com.kizitonwose.calendar.compose.rememberCalendarState
import com.kizitonwose.calendar.compose.weekcalendar.rememberWeekCalendarState
import com.kizitonwose.calendar.core.firstDayOfWeekFromLocale
import com.kizitonwose.calendar.core.minusDays
import com.kizitonwose.calendar.core.minusMonths
import com.kizitonwose.calendar.core.now
import com.kizitonwose.calendar.core.plusMonths
import com.skydoves.flexible.bottomsheet.material3.FlexibleBottomSheet
import com.skydoves.flexible.core.FlexibleSheetSize
import com.skydoves.flexible.core.rememberFlexibleBottomSheetState
import kotlinx.coroutines.launch
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.daysUntil
import kotlinx.datetime.plus
import kotlinx.datetime.yearMonth
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel
import ru.education.building.schedule.data.repositories.ScheduleRepository
import ru.education.building.schedule.view.viewmodels.ScheduleViewModel
import ru.education.building.schedule.widgets.CustomAppBar
import ru.education.building.schedule.widgets.ModalScreen
import ru.education.building.schedule.widgets.calendar.CustomCalendar
import ru.education.building.schedule.widgets.calendar.getFirstDayOfWeek
import ru.education.building.schedule.widgets.pager.CustomPager
import kotlin.time.ExperimentalTime


@OptIn(ExperimentalTime::class, ExperimentalMaterial3Api::class)
@Composable
fun SchedulePage() {
    val rp: ScheduleRepository = koinInject()
    val vm: ScheduleViewModel = koinViewModel()

    val currentDate = rememberSaveable { LocalDate.now() }
    val currentMonth = rememberSaveable { currentDate.yearMonth }
    val startMonth = rememberSaveable { currentMonth.minusMonths(8) }
    val endMonth = rememberSaveable { currentMonth.plusMonths(8) }
    val daysOfWeek = rememberSaveable { firstDayOfWeekFromLocale() }

    val monthState = rememberCalendarState(
        startMonth = startMonth,
        endMonth = endMonth,
        firstVisibleMonth = currentMonth,
        firstDayOfWeek = daysOfWeek
    )

    val weekState = rememberWeekCalendarState(
        startDate = startMonth.firstDay,
        endDate = endMonth.lastDay,
        firstVisibleWeekDate = currentDate,
        firstDayOfWeek = daysOfWeek
    )

    val lazyListState = rememberLazyListState()
    val coroutineScope = rememberCoroutineScope()

    var isWeekMode by rememberSaveable { mutableStateOf(true) }
    //var selectedGroup by rememberSaveable { mutableStateOf("") }
    var selectedDate by rememberSaveable { mutableStateOf(currentDate) }
    var currentWeekStart by rememberSaveable { mutableStateOf(selectedDate.getFirstDayOfWeek())  }
    var currentMonthStart by rememberSaveable { mutableStateOf(currentDate.yearMonth) }
    val totalDays = rememberSaveable { startMonth.firstDay.daysUntil(endMonth.lastDay) }
    val initialPage = rememberSaveable { startMonth.firstDay.daysUntil(currentDate)}

    val pagerState = rememberPagerState(
        initialPage = initialPage,
        pageCount = { totalDays }
    )

    LaunchedEffect(pagerState.currentPage) {
        // Задаем выбранную дату
        val newDate = startMonth.firstDay.plus(pagerState.currentPage, DateTimeUnit.DAY)
        selectedDate = newDate

        // Проверяем смену недели
        val newWeekStart = newDate.getFirstDayOfWeek()
        if (newWeekStart != currentWeekStart) {
            currentWeekStart = newWeekStart
            weekState.animateScrollToWeek(newDate)
        }

        // Проверяем смену месяца
        val newMonth = newDate.yearMonth
        if (newMonth != currentMonthStart || monthState.firstVisibleMonth.weekDays.last().last().date == selectedDate.minusDays(1)) {
            currentMonthStart = newMonth
            monthState.animateScrollToMonth(newMonth)
        }
    }

    LaunchedEffect(selectedDate) {
        val targetPage = startMonth.firstDay.daysUntil(selectedDate)
        if (targetPage in 0 until totalDays && targetPage != pagerState.currentPage) {
            pagerState.scrollToPage(targetPage)
        }
    }

    val sheetState = rememberFlexibleBottomSheetState(
        flexibleSheetSize = FlexibleSheetSize(fullyExpanded = 0.85f),
        isModal = true,
        skipIntermediatelyExpanded = true,
        skipSlightlyExpanded = true,
        allowNestedScroll = false,
    )
    var showBottomSheet by rememberSaveable { mutableStateOf(false) }

    if (showBottomSheet) {
        FlexibleBottomSheet(
            sheetState = sheetState,
            onDismissRequest = { showBottomSheet = false },
            dragHandle = {
                BottomSheetDefaults.DragHandle(
                    color = Color.Gray
                )
            }
        ) { ModalScreen(rp, vm) }
    }

    Column {
        CustomAppBar(
            selectedGroup = rp.selectedGroup ?: "",
            onTapShowSheet = { showBottomSheet = true },
            onTapShowCalendar = {
                isWeekMode = !isWeekMode

                coroutineScope.launch {
                    if (isWeekMode) {
                        if (selectedDate.yearMonth == monthState.firstVisibleMonth.yearMonth) {
                            val targetDate = selectedDate
                            weekState.scrollToWeek(targetDate)
                            weekState.animateScrollToWeek(targetDate) // Trigger a layout pass for title update
                        } else {
                            val targetDate = monthState.firstVisibleMonth.weekDays.last().last().date
                            weekState.scrollToWeek(targetDate)
                            weekState.animateScrollToWeek(targetDate) // Trigger a layout pass for title update
                        }
                    } else {
                        val targetMonth = weekState.firstVisibleWeek.days.first().date.yearMonth
                        monthState.scrollToMonth(targetMonth)
                        monthState.animateScrollToMonth(targetMonth) // Trigger a layout pass for title update
                    }
                }
            }
        )

        LazyColumn(state = lazyListState, overscrollEffect = null) {
            item {
                CustomCalendar(
                    isWeekMode = isWeekMode,
                    selectedDate = selectedDate,
                    monthState = monthState,
                    weekState = weekState,
                    onSelectedChanged = { day -> selectedDate = day }
                )
            }
            item {
                CustomPager(
                    items = rp.cachedSchedule,
                    pagerState = pagerState,
                    lazyListState = lazyListState,
                    startDate = startMonth.firstDay
                )
            }
        }
    }
}

